import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { requestPlan } from "../concierge/api";
import { applyPlan } from "../concierge/applyPlan";
import PlanPreview from "../concierge/PlanPreview";
import { trackOnboarding } from "../analytics";
import { CTA_GHOST, CTA_PRIMARY } from "../ui/theme";
import type { ProvisioningPlan } from "../concierge/types";
import type { OnboardingAnswers } from "../types";

interface StepConciergeProps {
  answers: OnboardingAnswers;
  onComplete: (appliedTemplateId: string) => void;
  /** Fall back to the manual template path. */
  onFallback: () => void;
}

type Phase = "loading" | "preview" | "applying" | "error";

/** The real stages behind the wait — shown so it reads as work, not a spinner. */
const STAGES = [
  "Reading your profile",
  "Matching the Newsquawk library",
  "Composing your dashboard",
] as const;

const BAR_HEIGHTS = [10, 18, 28, 20, 34, 22, 14, 26, 12];

/** Squawk waveform — the audio motif doubles as the working indicator. */
function Waveform() {
  return (
    <div className="flex h-9 items-center gap-[3px]" aria-hidden>
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={index}
          className="nq-onb-bar w-[3px] rounded-full bg-squawk-blue"
          style={{ height, ["--nq-i" as string]: index }}
        />
      ))}
    </div>
  );
}

export default function StepConcierge({
  answers,
  onComplete,
  onFallback,
}: StepConciergeProps) {
  const { hasAccessToWidget } = useAccessControl();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("loading");
  const [plan, setPlan] = useState<ProvisioningPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState(0);
  const requestedRef = useRef(false);

  // Advance the stage list while the request is in flight (typical 9-13s).
  useEffect(() => {
    if (phase !== "loading") return;
    const timers = [
      setTimeout(() => setStage(1), 3200),
      setTimeout(() => setStage(2), 6800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    trackOnboarding("concierge_requested");
    const query = [
      answers.freeText?.trim(),
      answers.assetClasses.length
        ? `(asset classes: ${answers.assetClasses.join(", ")})`
        : null,
    ]
      .filter(Boolean)
      .join(" ");
    requestPlan(query, "onboarding")
      .then((response) => {
        setPlan(response.plan);
        setPhase("preview");
      })
      .catch((err) => {
        console.error("[concierge] plan request failed:", err);
        setError(
          err?.response?.data?.detail ??
            "We couldn't build a plan right now — pick a template instead."
        );
        setPhase("error");
      });
  }, [answers]);

  const handleApply = async () => {
    if (!plan) return;
    setPhase("applying");
    try {
      const result = await applyPlan(plan, { hasAccessToWidget });
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
      trackOnboarding("concierge_applied");
      toast.success(
        result.tabTitle ? `"${result.tabTitle}" is ready` : "Your setup is ready"
      );
      onComplete("concierge");
    } catch (err) {
      console.error("[concierge] apply failed:", err);
      toast.error("Couldn't apply that — anything created is in Watchlists & Filters.");
      setPhase("preview");
    }
  };

  if (phase === "loading") {
    return (
      <div className="py-6">
        <Waveform />
        <div className="nq-onb-sweep relative mt-5 h-px w-full overflow-hidden bg-white/[0.07]" />
        <ul className="mt-6 space-y-2.5">
          {STAGES.map((label, index) => (
            <li
              key={label}
              className={`flex items-center gap-2.5 text-sm transition-colors duration-500 ${
                index <= stage ? "text-gray-200" : "text-gray-600"
              }`}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
                  index < stage
                    ? "border-squawk-blue bg-squawk-blue text-white"
                    : index === stage
                      ? "border-squawk-blue text-squawk-blue"
                      : "border-white/10"
                }`}
              >
                {index < stage && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />}
                {index === stage && (
                  <span className="h-1.5 w-1.5 rounded-full bg-squawk-blue" />
                )}
              </span>
              {label}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="py-6">
        <p className="text-sm text-gray-300">{error}</p>
        <button type="button" onClick={onFallback} className={`${CTA_PRIMARY} mt-5`}>
          Pick a template
        </button>
      </div>
    );
  }

  return (
    <div>
      {plan && <PlanPreview plan={plan} onChange={setPlan} />}

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleApply}
          disabled={phase === "applying"}
          className={CTA_PRIMARY}
        >
          {phase === "applying" ? "Setting up…" : "Apply this setup"}
        </button>
        <button
          type="button"
          onClick={onFallback}
          disabled={phase === "applying"}
          className={CTA_GHOST}
        >
          Use a template
        </button>
      </div>
    </div>
  );
}
